// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Filter, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { StoreSettingEntry } from "@/api/hooks/use-stores";
import { useStoreDetailQuery, useStoreSettingsQuery } from "@/api/hooks/use-stores";
import { SettingsCompareIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { KeyChip } from "@/components/shared/key-chip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SettingValueCell } from "@/features/settings/setting-value-cell";

interface StoreSettingsCompareProps {
  storeNameA: string;
  storeNameB: string;
}

interface CompareRow {
  settingId: number;
  key: string;
  name: string;
  fieldType: StoreSettingEntry["fieldType"];
  valueA?: string;
  sourceA?: StoreSettingEntry["overrideSource"];
  valueB?: string;
  sourceB?: StoreSettingEntry["overrideSource"];
  isDiff: boolean;
}

function effectiveValue(entry: StoreSettingEntry): string {
  return entry.overrideValue ?? entry.defaultValue;
}

function SourceBadge({ source }: { source: StoreSettingEntry["overrideSource"] }) {
  const colorMap: Record<StoreSettingEntry["overrideSource"], string> = {
    store: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    chain: "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
    default: "text-muted-foreground",
  };
  return (
    <Badge variant="secondary" className={colorMap[source]}>
      {source}
    </Badge>
  );
}

function CompareValueCell({
  value,
  source,
  fieldType,
  name,
}: {
  value?: string;
  source?: StoreSettingEntry["overrideSource"];
  fieldType: string;
  name: string;
}) {
  if (value === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <SettingValueCell value={value} fieldType={fieldType} name={name} />
      {source && <SourceBadge source={source} />}
    </div>
  );
}

export function StoreSettingsCompare({ storeNameA, storeNameB }: StoreSettingsCompareProps) {
  const { t } = useTranslation("back-office");
  const [onlyDiffs, setOnlyDiffs] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const flushSearch = useCallback((value: string) => {
    clearTimeout(debounceRef.current);
    setDebouncedSearch(value);
  }, []);

  function handleSearchInput(value: string) {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  }

  useEffect(function cleanupSearchDebounceTimer() {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const { data: storeARaw } = useStoreDetailQuery(storeNameA);
  const { data: storeBRaw } = useStoreDetailQuery(storeNameB);

  const storeA = storeARaw as Record<string, unknown> | undefined;
  const storeB = storeBRaw as Record<string, unknown> | undefined;
  const displayNameA = (storeA?.legibleName as string) ?? storeNameA;
  const displayNameB = (storeB?.legibleName as string) ?? storeNameB;

  const { data: settingsAData, isSuccess: settingsASuccess } = useStoreSettingsQuery({
    storeName: storeNameA,
    limit: -1,
  });

  const { data: settingsBData, isSuccess: settingsBSuccess } = useStoreSettingsQuery({
    storeName: storeNameB,
    limit: -1,
  });

  const settingsReady = settingsASuccess && settingsBSuccess;

  const breadcrumbs = useMemo(() => [{ label: t("stores.title"), to: "/stores" }], [t]);

  const rows = useMemo<CompareRow[]>(() => {
    const mapA = new Map<number, StoreSettingEntry>((settingsAData?.settings ?? []).map((s) => [s.settingId, s]));
    const mapB = new Map<number, StoreSettingEntry>((settingsBData?.settings ?? []).map((s) => [s.settingId, s]));

    const allIds = new Set([...mapA.keys(), ...mapB.keys()]);
    const result: CompareRow[] = [];

    for (const id of allIds) {
      const a = mapA.get(id);
      const b = mapB.get(id);
      const entry = (a ?? b)!;

      result.push({
        settingId: id,
        key: entry.key,
        name: entry.name,
        fieldType: entry.fieldType,
        valueA: a ? effectiveValue(a) : undefined,
        sourceA: a?.overrideSource,
        valueB: b ? effectiveValue(b) : undefined,
        sourceB: b?.overrideSource,
        isDiff: effectiveValue(a ?? entry) !== effectiveValue(b ?? entry) || !a || !b,
      });
    }

    result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [settingsAData, settingsBData]);

  const visibleRows = useMemo(() => {
    let filtered = rows;
    if (onlyDiffs) filtered = filtered.filter((r) => r.isDiff);
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      filtered = filtered.filter((r) => r.name.toLowerCase().includes(q) || r.key.toLowerCase().includes(q));
    }
    return filtered;
  }, [rows, onlyDiffs, debouncedSearch]);

  const isFiltered = !onlyDiffs || debouncedSearch.length > 0;

  return (
    <div>
      <PageHeader
        title={t("stores.compare.title")}
        icon={SettingsCompareIcon}
        subtitle={
          <>
            <KeyChip label={storeNameA} variant="store" />
            <span className="text-muted-foreground text-xs">vs</span>
            <KeyChip label={storeNameB} variant="store" />
          </>
        }
        breadcrumbs={breadcrumbs}
      />

      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <form
            className="relative"
            onSubmit={(e) => {
              e.preventDefault();
              flushSearch(search);
            }}
          >
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => handleSearchInput(e.target.value)}
              placeholder={t("settings.list.searchPlaceholder")}
              className="pl-9 w-[250px]"
            />
          </form>
          <FilterToggle
            active={onlyDiffs}
            onChange={setOnlyDiffs}
            icon={Filter}
            label={t("stores.compare.onlyDiffs")}
          />
          {isFiltered && (
            <Button
              variant="ghost"
              className="h-9 px-2"
              onClick={() => {
                setOnlyDiffs(true);
                setSearch("");
                flushSearch("");
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-sidebar">
                <th className="text-left h-10 px-3 font-medium text-sm w-[260px] rounded-tl-md">
                  {t("settings.columns.setting")}
                </th>
                <th className="text-left h-10 px-3 font-medium text-sm w-[220px] border-l">{displayNameA}</th>
                <th className="text-left h-10 px-3 font-medium text-sm w-[220px] border-l rounded-tr-md">
                  {displayNameB}
                </th>
              </tr>
            </thead>
            <tbody>
              {!settingsReady &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="border-b last:border-b-0">
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Skeleton className="h-5 w-32" />
                    </td>
                    <td className="px-3 py-2">
                      <Skeleton className="h-5 w-32" />
                    </td>
                  </tr>
                ))}
              {settingsReady && visibleRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-center py-8 text-muted-foreground">
                    {onlyDiffs && !debouncedSearch ? t("stores.compare.noDifferences") : t("common.noResults")}
                  </td>
                </tr>
              )}
              {settingsReady &&
                visibleRows.map((row) => (
                  <tr
                    key={row.settingId}
                    className={
                      !onlyDiffs && row.isDiff
                        ? "bg-amber-50 dark:bg-amber-950/20 border-b last:border-b-0"
                        : "border-b last:border-b-0 hover:bg-muted/30"
                    }
                  >
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <span>{row.name}</span>
                        <KeyChip label={row.key} />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <CompareValueCell
                        value={row.valueA}
                        source={row.sourceA}
                        fieldType={row.fieldType}
                        name={row.name}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <CompareValueCell
                        value={row.valueB}
                        source={row.sourceB}
                        fieldType={row.fieldType}
                        name={row.name}
                      />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
